import { Component, OnDestroy, OnInit } from '@angular/core';
import { concatMap, filter, pairwise } from '../../node_modules/rxjs/operators';
import { HttpErrorResponse } from '../../node_modules/@angular/common/http';
import { MenuItem } from 'primeng/api/menuitem';
import { Router, RoutesRecognized } from '@angular/router';
import { ScfCommunicationService, ScfNotification, ScfToastrService } from 'scf-library';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';

import { AuthService } from './shared/authentication/services/auth.service';
import { ChangePasswordData } from './administration/change-password/change-password-data';
import { CONSTANTS } from './shared/constants';
import { ENVIRONMENT } from '../environments/environment';
import { IMenuInterface } from './models/menu-interface';
import { LabelService } from './administration/label-management/label.service';
import { LANGUAGES_LIST_MOCK } from './shared/scf/language.mock';
import { MobileConfig } from './shared/constants/mobile-config';
import { PolicyParam } from './administration/policy-param/policy-param';
import { PolicyParamService } from './administration/policy-param/policy-param.service';
import { ReleaseTask } from './shared/interfaces/release-task';
import { ReleaseTaskService } from './shared/services/release-task.service';
import { Session } from './session/session';
import { SessionService } from './session/session.service';
import { SidebarWrapperService } from './shared/mobile-sidebar/sidebar-wrapper.service';
import { SocketEvents } from './shared/services/sockets/socket-events';
import { SocketService } from './shared/services/sockets/socket.service';
import { SocketSession } from './shared/services/sockets/models/socket-session';
import { TaskGenericWrapperService } from './shared/templates/html/task-wrapper/task-wrapper.service';
import { TaskStatusEnum } from './process/task/task-enums';
import { User } from './administration/user/user';
import { WepError } from './shared/wep-error';

@Component({
  selector: 'app-pages-component',
  templateUrl: './pages.component.html'
})
export class PagesComponent implements OnInit, OnDestroy {
  private previousRoute: string;
  public currentRoute: string;
  public customMediaMatch: string;
  public display: boolean;
  public dockedSize: string;
  public hideChangePassword: boolean;
  public hideViewProfile: boolean;
  public isLoggedIn: boolean;
  public languages: any[];
  public lbl: any;
  public menu: IMenuInterface[];
  public mobile: boolean;
  public mode: string;
  public msgs: any;
  public opened: boolean;
  public owner: string;
  public position: string;
  public showBackDrop: boolean;
  public solution: string;
  public trapFocus: boolean;
  public userInfo: any;
  public userSettingsBackEl: any;
  public userSettingsItems: MenuItem[];
  private onSocketConnection: Subscription;

  constructor(private authService: AuthService,
    private labelService: LabelService,
    private notifier: ScfToastrService,
    private releaseTaskService: ReleaseTaskService,
    private router: Router,
    private scfComService: ScfCommunicationService,
    private sidebarWrapperService: SidebarWrapperService,
    private taskWrapperService: TaskGenericWrapperService,
    private socketService: SocketService,
    private sessionService: SessionService,
    private policyParamService: PolicyParamService) {
    this.currentRoute = null;
    this.hideChangePassword = false;
    this.hideViewProfile = true;
    this.isLoggedIn = this.isAuthenticated();
    this.lbl = {
      info: this.labelService.labelText('infoPages'),
      sessionExpired: this.labelService.labelText('sessionExpired'),
      successReleaseTask: this.labelService.labelText('taskWrapperUnSelectTask'),
      successTitle: this.labelService.labelText('infoTitle')
    };
    this.mobile = ENVIRONMENT.ALWAYS_ON_MOBILE ? ENVIRONMENT.ALWAYS_ON_MOBILE : window.screen.width <= MobileConfig.WINDOW_WIDTH;
    this.msgs = {
      userInfoError: {
        tlt: this.labelService.labelText('errorTitle'),
        msg: this.labelService.labelText('sidebarUserInfoError')
      },
      releaseTaskError: {
        tlt: this.labelService.labelText('errorTitle'),
        msg: this.labelService.labelText('releaseTaskError')
      },
      socketDisconnect: {
        msg: this.labelService.labelText('socketDisconnectMessage')
      }
    };
    this.buildMenu();
    this.display = false;
    this.languages = LANGUAGES_LIST_MOCK;
    this.opened = false;
    this.owner = '';
    this.previousRoute = null;
    this.showBackDrop = true;
    this.solution = CONSTANTS.WEP;
    this.trapFocus = true;
    this.userInfo = { name: '' };
  }

  /**
   * @description unSubscribe from socket conection event to avoid duplicated observers
   * @return {void}
   */
  ngOnDestroy(): void {
    if (!_.isNil(this.onSocketConnection)) {
      this.onSocketConnection.unsubscribe();
    }
  }

  /**
   * @description App init Component.
   * @return {void}
   */
  public ngOnInit(): void {
    this.subscribeLoggedStatus();
    this.subscribeRoutes();
    this.getLoggedUser();
    this.subscribeToScfEvents();
    this.socketConfiguration();
    this.checkMobileConfig();
  }

  /**
   * @description Subscribes to the observable that indicated if the user is logged-in or not
   * @return {void}
   */
  public subscribeLoggedStatus(): void {
    this.authService.getLoggedInObservable().subscribe(
      (isLoggedIn: boolean) => {
        if (isLoggedIn !== undefined) {
          this.isLoggedIn = isLoggedIn;
          if (isLoggedIn === false) {
            this.authService.logout(true);
          }
          this.setWrapperClass();
        }
      });
  }

  /**
   * @description Returns true if the token stored in the localstorage key 'id_token' is not expired and false
   * if it has expired or it doesn't exist.
   * @return {boolean}
   */
  public isAuthenticated(): boolean {
    if (this.authService.isAuthenticated()) {
      return true;
    }
    if (this.previousRoute !== null && _.isEqual(this.router.url, CONSTANTS.LOGIN_URL)) {
      this.previousRoute = null;
      this.notifier.infoAlert(this.lbl.sessionExpired);
    }
    if (_.isEqual(this.router.url, CONSTANTS.LOGIN_URL)) {
      return false;
    } else {
      this.authService.logout();
    }
    return false;
  }

  /**
   * @description Set class for page-content-wrapper
   * @return {string} Wrapper class
   */
  public setWrapperClass(): string {
    let wClass = '';
    return wClass;
  }

  /**
   * @description Subscribes to the Router Service and switches current and previous route accordingly
   * @return {void}
   */
  private subscribeRoutes(): void {
    this.router.events
      .pipe(filter(event => event instanceof RoutesRecognized))
      .pipe(pairwise())
      .subscribe((events: any[]) => {
        this.currentRoute = events.pop().url;
        this.previousRoute = events.pop().url;
      });
  }

  /**
   * @description Navigate to a previously route selected.
   * @param {Array<String>} routerLink routerLink property to navigate.
   * @return {void}
   */
  public navigateTo(routerLink: Array<string>): void {
    this.router.navigate(routerLink);
  }

  /**
   * @description Build the sidebar Menu
   * @return {void}
   */
   public buildMenu(): void {
    this.menu = this.authService.getAllowedRoutes();
    if (this.mobile) {
      _.remove(this.menu, (mobileRoutes) => _.isUndefined(mobileRoutes.mobile));
    }
  }

  /**
   * @description Configurate user to warehouse room
   * @return {void}
   */
  public socketConfiguration(): void {
    let session: Session =  this.sessionService.getUserSession();
    let socketSession: SocketSession = new SocketSession();
    socketSession.userId = session.userId;
    socketSession.warehouseId = session.warehouseId;
    this.socketService.setSocketSession(socketSession);
    this.onSocketConnection = this.socketService.listen(SocketEvents.CONNECT).subscribe(
      () => {
        if (!_.isNil(session.warehouseId)) {
          this.socketService.setSocketSession(socketSession);
        }
      }
    );
    this.socketService.listen(SocketEvents.DISCONNECT).subscribe(
      (reason: string) => {
        if (_.isEqual(reason, CONSTANTS.PING_TIMEOUT_REASON)) {
          this.notifier.infoAlert(this.msgs.socketDisconnect.msg);
        }
      }
    );
  }

  /**
   * @description Check mobile configuration.
   * @return {void}
   */
  public checkMobileConfig(): void {
    if (ENVIRONMENT.ALWAYS_ON_MOBILE) {
      this.customMediaMatch = CONSTANTS.CUSTOM_MATCHER;
    }
  }

  /**
   * @description Gets user logged information.
   * @return {void}
   */
  public getLoggedUser(): void {
    let user: User = new User();
    const daysPasswordChangeParam: PolicyParam = this.initParameter(
      CONSTANTS.DAYS_PASSWORD_CHANGE, CONSTANTS.GENERAL_MODULE_STRING);
    this.authService.getUserInfo().pipe(
      concatMap((userFound: User) => {
        user = userFound;
        return this.policyParamService.getParameterRelatedToUserAccounts(daysPasswordChangeParam);
      })
    ).subscribe((daysPasswordChange: PolicyParam) => {
        this.userInfo.name = user.name + ' ' + user.surname;
        this.owner = this.userInfo.name;
        this.authService.setTimeZone(user.profile.timeZone);
        const changePasswordData: ChangePasswordData = new ChangePasswordData()
          .initChangePasswordDataByUser(user, _.toInteger(daysPasswordChange.value));
        this.authService.sendChangePasswordData(changePasswordData);
        this.authService.showUpdatePasswordMessage();
      }, (error: HttpErrorResponse) => {
        if (_.isEqual(error.status, CONSTANTS.AUTHORIZATION)) {
          this.authService.logout();
        }
      });
  }

  /**
   * @description This method redirect to login screen
   * @return {void}
   */
  public logOutUser(): void {
    this.validateReleaseTask();
  }

  /**
   * @description validate release task.
   * @return {void}
   */
  private validateReleaseTask(): void {
    this.releaseTaskService.getReleaseTaskAsObservable().subscribe((taskToRelease: ReleaseTask) => {
      if (!_.isUndefined(taskToRelease)) {
        if (_.isEqual(taskToRelease.component, CONSTANTS.PICKING_LIST_COMPONENT) &&
          _.isEqual(taskToRelease.status, TaskStatusEnum.Assigned)) {
          this.taskWrapperService.releasePickingList(taskToRelease).subscribe(() => {
            this.notifier.infoAlert(this.lbl.successReleaseTask);
            this.authService.logout();
            this.releaseTaskService.releaseObservable();
          },
            (error: WepError) => {
              this.notifier.errorAlert(this.msgs.releaseTaskError.msg);
            });
        } else {
          this.authService.logout();
        }
      } else {
        this.authService.logout();
      }
    });
  }

  /**
   * @description Subscribes to SCF Events
   * @return {void}
   */
  private subscribeToScfEvents(): void {
    // Notification Ceter
    this.scfComService.notificationReadSubscribe()
      .subscribe((notification: ScfNotification) => {
        // TODO: @rperez 2021/01/01 Put logic about notification center here.
      });

    // Sidebar & Search Box navigate to
    this.scfComService.navigateToSubscribe()
      .subscribe((routerLink: string) => {
        if (routerLink) {
          this.router.navigate([routerLink]);
        }
      });

    // User's panel solution selected
    this.scfComService.solutionChangedSubscribe()
      .subscribe((solution: string) => {
        if (solution) {
          this.solution = solution;
        }
      });

    // Goes to change password component
    this.scfComService.passwordChangeSubscribe()
      .subscribe(() => {
        this.authService.goesToChangePassword(true);
      });

    // Logout Service
    this.scfComService.logoutSubscribe()
      .subscribe((navRequest: any) => {
        if (!navRequest) {
          this.logOutUser();
        }
      });

    // Sidebar Service
    this.scfComService.sidebarSubscribe()
      .subscribe(() => {
        if (this.mobile) {
          this.display = !this.display;
          this.sidebarWrapperService.sidebarWrapperToggle(this.display);
        }
      });
  }

  /**
   * @description Breakpoint to validate if menu closes
   * @return {void}
   */
  public closePanelNotify(): void {
    this.display = false;
  }

  /**
   * @description Generates an instance of a parameter
   * @param {string} name - Name of parameter
   * @param {string} module - Name of module
   * @returns {PolicyParam} - Object mapped
   */
  private initParameter(name: string, module: string): PolicyParam {
    const parameter: PolicyParam = new PolicyParam();
    parameter.name = name;
    parameter.module.name = module;
    return parameter;
  }
}
